import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { getHubBrowserClient } from "@/lib/supabase/typed-browser-client";
import type { ProposalStatus } from "@/lib/types/contracts";

export function useBulkUpdateContractStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      contractIds,
      status,
    }: {
      contractIds: string[];
      status: ProposalStatus;
    }) => {
      const supabase = getHubBrowserClient();
      const { data, error } = await supabase
        .from("contact_proposals")
        .update({
          status,
          updated_at: new Date().toISOString(),
        })
        .in("id", contractIds)
        .select();

      if (error) {
        throw new Error(error.message);
      }

      return data;
    },
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: ["contact-proposals"] });
      void queryClient.invalidateQueries({ queryKey: ["user-proposals"] });
      toast.success(`Updated ${data?.length ?? 0} contract(s)`);
    },
    onError: (error: Error) => {
      toast.error(`Failed to update contracts: ${error.message}`);
    },
  });
}

export function useBulkDeleteContracts() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (contractIds: string[]) => {
      const supabase = getHubBrowserClient();
      const { data, error } = await supabase
        .from("contact_proposals")
        .delete()
        .in("id", contractIds)
        .select("id");

      if (error) {
        throw new Error(error.message);
      }

      return data;
    },
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: ["contact-proposals"] });
      void queryClient.invalidateQueries({ queryKey: ["user-proposals"] });
      toast.success(`Deleted ${data?.length ?? 0} contract(s)`);
    },
    onError: (error: Error) => {
      toast.error(`Failed to delete contracts: ${error.message}`);
    },
  });
}

export function useBulkExportContracts() {
  return useMutation({
    mutationFn: async (contractIds: string[]) => {
      const supabase = getHubBrowserClient();
      const { data, error } = await supabase
        .from("contact_proposals")
        .select(
          `
          *,
          contacts:contact_id (
            name,
            email,
            company_name
          )
        `
        )
        .in("id", contractIds);

      if (error) {
        throw new Error(error.message);
      }

      if (!data || data.length === 0) {
        throw new Error("No contracts found to export");
      }

      // Import CSV utilities dynamically
      const { objectsToCSV, downloadCSV, formatDateForCSV, formatCurrencyForCSV } =
        await import("@/lib/utils/csv-export");

      // Transform data for export
      const exportData = data.map((contract) => ({
        ID: contract.id,
        Title: contract.title || "",
        Description: contract.description || "",
        Status: contract.status || "",
        Amount: formatCurrencyForCSV(contract.amount),
        Currency: contract.currency || "",
        "Client Name": contract.contacts?.name || "",
        "Client Email": contract.contacts?.email || "",
        "Company Name": contract.contacts?.company_name || "",
        "File Name": contract.file_name || "",
        "Sent At": formatDateForCSV(contract.sent_at),
        "Created At": formatDateForCSV(contract.created_at),
        "Updated At": formatDateForCSV(contract.updated_at),
        Notes: contract.notes || "",
      }));

      // Generate CSV
      const csvContent = objectsToCSV(exportData);
      const timestamp = new Date().toISOString().split("T")[0];
      downloadCSV(csvContent, `contracts_export_${timestamp}.csv`);

      return { exported: data.length };
    },
    onSuccess: (data) => {
      toast.success(`Exported ${data.exported} contract(s)`);
    },
    onError: (error: Error) => {
      toast.error(`Failed to export contracts: ${error.message}`);
    },
  });
}
